<template>
    <div class="flex flex-column justify-content-center align-items-center w-full h-full">
        <Form class=" form">
            <h4 class="Paragraph-Medium text-left">Previous Experiences</h4>
            <div class="w-full">
                <div v-for="(exp, index) in experiences" :key="index" class="col-12 grid">
                    <div class="col-12 md:col-6">
                        <div class="flex flex-column gap-3 pt-2 relative mb-5">
                            <label class="Label align-self-start" :for="'institution-' + index">
                                Institution/School:
                            </label>
                            <InputText v-model="exp.institution" :id="'institution-' + index"
                                placeholder="Enter institution/school name" size="small" variant="filled"
                                class="w-full" />
                            <small v-if="errors['inst_' + index]" class="error-text">{{ errors['inst_' + index] }}</small>
                        </div>
                    </div>

                    <div class="col-12 md:col-6">
                        <div class="flex gap-2 align-items-end">
                            <div class="flex flex-column gap-3 pt-2 relative mb-5 flex-1">
                                <label class="Label align-self-start" :for="'experienceYears-' + index">
                                    Years of teaching experience:
                                </label>
                                <InputNumber v-model="exp.yearsOfExperience" :id="'experienceYears-' + index" size="small"
                                    variant="filled" class="w-full" />
                                <small v-if="errors['years_' + index]" class="error-text">{{ errors['years_' + index] }}</small>
                            </div>
                            <Button size="small" icon="pi pi-times" severity="danger" aria-label="Remove"
                                variant="outlined" @click="removeExperience(index)" v-if="index > 0"
                                class="mb-5 flex-shrink-0" style="width: 2rem; min-width: 2rem;" />
                        </div>
                    </div>
                    <!-- <Divider class="w-full" /> -->
                </div>

                <!-- Add button -->
                <div class="col-12 md:col-6 p-0 pl-2 pb-2">
                    <div class="flex justify-content-start mb-4 pt-2">
                        <Button label="Add Experience" class="teal-button mt-2" @click="addExperience" />
                    </div>
                </div>
                <Divider class="w-full pb-3" />
            </div>

            <div class="grid w-full">
                <!-- Highest Education -->
                <div class="flex flex-column col-12 justify-content-start gap-2 pt-4">
                    <div class="grid align-items-center mb-3" v-for="(edu, index) in highestEducations" :key="'edu-' + index">
                        <div class="col-12 md:col-3">
                            <label v-if="index === 0" class="Label" :for="'highestEdu-' + index">
                                Higher education qualifications:
                            </label>
                        </div>
                        <div class="col-12 md:col-9">
                            <div class="flex gap-2 align-items-center">
                                <Button size="small" icon="pi pi-times" severity="danger" aria-label="Remove" variant="outlined"
                                    @click="removeHighestEducation(index)" v-if="index > 0" />
                                <div class="flex-1 relative">
                                    <InputText v-model="edu.education" size="small" :id="'highestEdu-' + index"
                                        variant="filled" class="w-full"
                                        placeholder="Enter highest education qualification here" />
                                    <small v-if="errors['highestEdu_' + index]" class="error-text">{{ errors['highestEdu_' + index] }}</small>
                                </div>
                                <div class="relative">
                                    <FileUpload :chooseLabel="edu.certificationURL ? 'Uploaded' : 'Upload'"
                                        :chooseIcon="edu.certificationURL ? 'pi pi-check' : 'pi pi-upload'"
                                        :severity="edu.certificationURL ? 'success' : 'secondary'"
                                        @select="uploadHighestCertificate($event, index)" mode="basic" customUpload auto
                                        class="teal-button-transparent mobile-icon-btn" />
                                    <small v-if="errors['eduCert_' + index]" class="error-text">{{ errors['eduCert_' + index] }}</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-12">
                    <div class="flex justify-content-start py-2">
                        <Button label="Add" class="teal-button" @click="addHighestEducation" />
                    </div>
                </div>

                <!-- Teaching Qualifications -->
                <div class="flex flex-column col-12 justify-content-start gap-2 pt-3">
                    <div class="grid align-items-center mb-2" v-for="(qualification, index) in qualifications"
                        :key="'qual-' + index">
                        <div class="col-12 md:col-3">
                            <label v-if="index === 0" class="Label" :for="'teachingEdu-' + index">
                                Teaching qualifications (if any):
                            </label>
                        </div>
                        <div class="col-12 md:col-9">
                            <div class="flex gap-2 align-items-center">
                                <Button size="small" icon="pi pi-times" severity="danger" aria-label="Cancel" variant="outlined"
                                    @click="removeQualification(index)" v-if="index > 0" />
                                <div class="flex-1 relative">
                                    <InputText v-model="qualification.qualification" size="small"
                                        :id="'teachingEdu-' + index" variant="filled" class="w-full"
                                        placeholder="Enter teaching qualification here" />
                                    <small v-if="errors['qual_' + index]" class="error-text">{{ errors['qual_' + index] }}</small>
                                </div>
                                <div class="relative">
                                    <FileUpload :chooseLabel="qualification.certificateURL ? 'Uploaded' : 'Upload'"
                                        :chooseIcon="qualification.certificateURL ? 'pi pi-check' : 'pi pi-upload'"
                                        :severity="qualification.certificateURL ? 'success' : 'secondary'"
                                        @select="uploadTeaching($event, index)" mode="basic" customUpload auto
                                        class="teal-button-transparent mobile-icon-btn" />
                                    <small v-if="errors['qualCert_' + index]" class="error-text">{{ errors['qualCert_' + index] }}</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-12">
                    <div class="flex justify-content-start py-2">
                        <Button label="Add" class="teal-button" @click="addQualification" />
                    </div>
                </div>
                <Divider class="w-full" />

                <div class="flex flex-column col-12 justify-content-start gap-2 pt-4">
                    <!-- Notable Achievements -->
                    <div class="grid align-items-center mb-2" v-for="(achievement, index) in achievements" :key="index">
                        <div class="col-12 md:col-3">
                            <label v-if="index === 0" class="Label" :for="'achievement-' + index">
                                Notable Achievements & Certifications:
                            </label>
                        </div>
                        <div class="col-12 md:col-9">
                            <div class="flex gap-2 align-items-center">
                                <Button size="small" icon="pi pi-times" severity="danger" aria-label="Remove"
                                    variant="outlined" @click="removeAchievement(index)" v-if="index > 0" />
                                <div class="flex-1 relative">
                                    <InputText v-model="achievement.achievement" size="small"
                                        :id="'achievement-' + index" variant="filled" class="w-full"
                                        placeholder="Enter achievement or certification here" />
                                </div>
                                <div class="relative">
                                    <FileUpload
                                        :chooseLabel="achievement.uploadURL ? 'Uploaded' : 'Upload'"
                                        :chooseIcon="achievement.uploadURL ? 'pi pi-check' : 'pi pi-upload'"
                                        :severity="achievement.uploadURL ? 'success' : 'secondary'"
                                        @select="uploadAchievement($event, index)" mode="basic" customUpload auto
                                        class="teal-button-transparent mobile-icon-btn" />
                                    <small v-if="!achievement.uploadURL" class="text-500 absolute w-full text-center" style="font-size: 0.7rem; top: 100%; margin-top: 2px;">optional</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12">
                    <div class="flex justify-content-start py-2">
                        <Button label="Add" class="teal-button" @click="addAchievement" />
                    </div>
                </div>
            </div>

            <div class="col-12 col-6 flex justify-content-between p-0">
                <div class=" align-items-center gap-3 ">
                    <Button label="Back" class="teal-button-transparent mt-6" @click="Cancel" />
                </div>
                <div class=" align-items-center gap-3 ">
                    <Button label="Next" class="teal-button mt-6" @click="Next" />
                </div>
            </div>

        </Form>
    </div>
</template>

<script setup>
import { ref, defineEmits, watch, onMounted } from "vue";
import { Form } from "@primevue/forms";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Divider from "primevue/divider";
import FileUpload from 'primevue/fileupload';
import { uploadCertificates, uploadAchievements } from "@/api/storage";
import { notifyError, notifyInfo } from "@/utils/notification";
import { useAuthStore } from "@/stores/authStore";

const emit = defineEmits(["submit-experiance-info", "previous-step"]);

// --- STATE ---
const errors = ref({}); // To hold validation errors

const experiences = ref([
    { institution: "", yearsOfExperience: null }
]);
const qualifications = ref([
    { qualification: "", certificateURL: "" }
]);
const highestEducations = ref([{ education: "", certificationURL: "" }]);
const achievements = ref([{ achievement: "", uploadURL: "" }]);

const authStore = useAuthStore();

onMounted(() => {
    const user = authStore.user;
    if (user) {
        // Pre-fill experiences
        if (user.experience && user.experience.length > 0) {
            experiences.value = user.experience.map(exp => ({
                institution: exp.institution || "",
                yearsOfExperience: exp.yearsOfExperience || null
            }));
        }

        // Pre-fill highest education
        if (user.highestEducation && user.highestEducation.length > 0) {
            highestEducations.value = user.highestEducation.map(e => ({
                education: e.education || "",
                certificationURL: e.certificationURL || ""
            }));
        } else {
            highestEducations.value = [{ education: "", certificationURL: "" }];
        }

        // Pre-fill teaching qualifications
        if (user.teachingQualifications && user.teachingQualifications.length > 0) {
            qualifications.value = user.teachingQualifications.map(qual => ({
                qualification: qual.qualification || "",
                certificateURL: qual.certificateURL || ""
            }));
        } else {
            // Ensure there's at least one empty row if no data is present
            qualifications.value = [{ qualification: "", certificateURL: "" }];
        }

        // Pre-fill notable achievements
        if (user.notableAchievements && user.notableAchievements.length > 0) {
            achievements.value = user.notableAchievements.map(a => ({
                achievement: a.achievement || "",
                uploadURL: a.uploadURL || ""
            }));
        } else {
            achievements.value = [{ achievement: "", uploadURL: "" }];
        }
    }
});

// --- WATCHERS TO CLEAR ERRORS ---
watch(highestEducations, (list) => {
    list.forEach((edu, index) => {
        if (edu.education) errors.value[`highestEdu_${index}`] = null;
        if (edu.certificationURL) errors.value[`eduCert_${index}`] = null;
    });
}, { deep: true });

watch(experiences, (newExpList) => {
    newExpList.forEach((exp, index) => {
        if (exp.institution) {
            errors.value[`inst_${index}`] = null;
        }
        if (exp.yearsOfExperience && exp.yearsOfExperience > 0) {
            errors.value[`years_${index}`] = null;
        }
    });
}, { deep: true });

watch(qualifications, (newQualList) => {
    newQualList.forEach((qual, index) => {
        if (qual.qualification) {
            errors.value[`qual_${index}`] = null;
        }
        if (qual.certificateURL) {
            errors.value[`qualCert_${index}`] = null;
        }
    });
}, { deep: true });


// --- METHODS ---
const addExperience = () => {
    experiences.value.push({ institution: "", yearsOfExperience: null });
};

const removeExperience = (index) => {
    experiences.value.splice(index, 1);
};

const addHighestEducation = () => {
    highestEducations.value.push({ education: "", certificationURL: "" });
};

const removeHighestEducation = (index) => {
    highestEducations.value.splice(index, 1);
};

const uploadHighestCertificate = async (event, index) => {
    const file = event.files[0];
    const url = await uploadFiles(file);
    if (url) {
        highestEducations.value[index].certificationURL = url;
        notifyInfo('File uploaded successfully.');
    }
};

const addQualification = () => {
    qualifications.value.push({ qualification: "", certificateURL: "" });
};

const uploadTeaching = async (event, index) => {
    const file = event.files[0];
    const url = await uploadFiles(file);
    if (url) {
        qualifications.value[index].certificateURL = url;
        notifyInfo('File uploaded successfully.');
    }
};

const removeQualification = (index) => {
    qualifications.value.splice(index, 1);
};

const addAchievement = () => {
    achievements.value.push({ achievement: "", uploadURL: "" });
};

const removeAchievement = (index) => {
    achievements.value.splice(index, 1);
};

const uploadAchievement = async (event, index) => {
    const file = event.files[0];
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await uploadAchievements(formData);
        achievements.value[index].uploadURL = response.data.url;
        notifyInfo('File uploaded successfully.');
    } catch {
        notifyError('File upload failed. Please try again.');
    }
};

const uploadFiles = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
        const response = await uploadCertificates(formData);
        return response.data.url;
    } catch (error) {
        notifyError('File upload failed. Please try again.');
        return null;
    }
};

const Cancel = () => {
    emit("previous-step");
};

// --- VALIDATION & SUBMIT ---
const validateForm = () => {
    errors.value = {};

    // Validate Experiences
    experiences.value.forEach((exp, index) => {
        if (!exp.institution) {
            errors.value[`inst_${index}`] = 'Institution is required';
        }
        if (!exp.yearsOfExperience || exp.yearsOfExperience <= 0) {
            errors.value[`years_${index}`] = 'Valid years of experience are required';
        }
    });

    // Validate Highest Education
    if (!highestEducations.value.some(e => e.education?.trim())) {
        errors.value['highestEdu_0'] = 'At least one education entry is required';
    }
    highestEducations.value.forEach((edu, index) => {
        if (edu.education?.trim() && !edu.certificationURL) {
            errors.value[`eduCert_${index}`] = 'Certificate upload is required';
        }
    });

    // Validate Teaching Qualifications
    qualifications.value.forEach((qual, index) => {
        if (qual.qualification && !qual.certificateURL) {
            errors.value[`qualCert_${index}`] = 'Certificate upload is required for this qualification';
        }
    });

    return Object.keys(errors.value).length === 0; // Returns true if no errors
};


const Next = () => {
    // Validate first
    if (!validateForm()) {
        return; // Stop execution
    }

    // Validation passed
    const payload = {
        experience: experiences.value.map(exp => ({
            institution: exp.institution,
            yearsOfExperience: exp.yearsOfExperience
        })),
        highestEducation: highestEducations.value
            .filter(e => e.education)
            .map(e => e.certificationURL ? { education: e.education, certificationURL: e.certificationURL } : { education: e.education }),
        teachingQualifications: qualifications.value.filter(qual => qual.qualification).map(qual => ({
            qualification: qual.qualification,
            certificateURL: qual.certificateURL
        })),
        notableAchievements: achievements.value
            .filter(a => a.achievement)
            .map(a => a.uploadURL ? { achievement: a.achievement, uploadURL: a.uploadURL } : { achievement: a.achievement })
    }
    emit("submit-experiance-info", payload);
};
</script>

<style scoped>
.form {
    width: 100%;
    min-width: 60vw;
}

@media (max-width: 768px) {
    .form {
        min-width: none;
    }

}

/* Added for non-shifting error messages */
.error-text {
    color: #dc2626;
    font-size: 0.85rem;
    position: absolute;
    top: 100%;
    margin-top: 0.5rem;
    left: 0;
    /* padding-left: 0.5rem; */
    white-space: nowrap;
}
</style>

<style>
@media (max-width: 768px) {
    .mobile-icon-btn .p-button-label {
        display: none !important;
    }
    .mobile-icon-btn .p-button {
        min-width: 0 !important;
        padding: 0.375rem !important;
    }
    .mobile-icon-btn .p-button-icon {
        margin: 0 !important;
    }
}
</style>